'use client';

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import RegistrationSuccessModal from '../components/RegistrationSuccessModal';
import { toast } from 'sonner';

const institutionTypes = [
	'BANK',
	'FINTECH',
	'MICROFINANCE',
	'COOPERATIVE',
	'STARTUP',
	'OTHER',
];

const Register = () => {
	const [loading, setLoading] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm();

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			const response = await apiService.post(
				'/institution/register',
				data,
			);

			if (response.data.status === 201) {
				setShowSuccessModal(true);
				toast.success(
					'Registration successful! Please check your email for credentials.',
				);
			} else {
				throw new Error(response.data.message || 'Registration failed');
			}
		} catch (error) {
			console.error('Registration error:', error);
			const message =
				error.response?.data?.message ||
				error.message ||
				'Registration failed. Please try again.';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	const handleCloseModal = () => {
		setShowSuccessModal(false);
		navigate('/login');
	};

	return (
		<>
			<div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
				<div className='max-w-md w-full space-y-8'>
					<div className='text-center'>
						<div className='mx-auto h-16 w-16 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg'>
							<span className='text-white font-bold text-2xl'>
								K
							</span>
						</div>
						<h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
							Create your account
						</h2>
						<p className='mt-2 text-center text-sm text-gray-600'>
							Or{' '}
							<Link
								to='/login'
								className='font-medium text-primary-600 hover:text-primary-500 transition-colors'>
								sign in to existing account
							</Link>
						</p>
					</div>

					<form
						className='mt-8 space-y-6'
						onSubmit={handleSubmit(onSubmit)}
						noValidate>
						<div className='space-y-4'>
							{/* Institution Name */}
							<div>
								<label
									htmlFor='name'
									className='block text-sm font-medium text-gray-700'>
									Institution Name *
								</label>
								<input
									{...register('name', {
										required:
											'Institution name is required',
									})}
									type='text'
									className='input-field mt-1'
									placeholder='Enter institution name'
								/>
								{errors.name && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.name.message}
									</p>
								)}
							</div>

							{/* Institution Type Dropdown */}
							<div>
								<label
									htmlFor='type'
									className='block text-sm font-medium text-gray-700'>
									Institution Type *
								</label>
								<select
									{...register('institutionType', {
										required:
											'Institution type is required',
									})}
									className='input-field mt-1'>
									<option value=''>Select type</option>
									{institutionTypes.map((type) => (
										<option
											key={type}
											value={type}>
											{type}
										</option>
									))}
								</select>
								{errors.type && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.type.message}
									</p>
								)}
							</div>

							{/* Contact Email */}
							<div>
								<label
									htmlFor='contactEmail'
									className='block text-sm font-medium text-gray-700'>
									Contact Email *
								</label>
								<input
									{...register('contactEmail', {
										required: 'Email is required',
										pattern: {
											value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
											message: 'Invalid email address',
										},
									})}
									type='email'
									className='input-field mt-1'
									placeholder='Enter contact email'
								/>
								{errors.contactEmail && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.contactEmail.message}
									</p>
								)}
							</div>

							{/* Contact Phone */}
							<div>
								<label
									htmlFor='contactPhone'
									className='block text-sm font-medium text-gray-700'>
									Contact Phone
								</label>
								<input
									{...register('contactPhone')}
									type='tel'
									className='input-field mt-1'
									placeholder='Enter contact phone (optional)'
								/>
							</div>

							{/* Callback URL */}
							<div>
								<label
									htmlFor='callbackUrl'
									className='block text-sm font-medium text-gray-700'>
									Callback URL *
								</label>
								<input
									{...register('callbackUrl', {
										required: 'Callback URL is required',
									})}
									type='url'
									className='input-field mt-1'
									placeholder='https://your-domain.com/callback'
								/>
								{errors.callbackUrl && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.callbackUrl.message}
									</p>
								)}
							</div>

							{/* Client Secret */}
							<div>
								<label
									htmlFor='clientSecret'
									className='block text-sm font-medium text-gray-700'>
									Client Secret *
								</label>
								<input
									{...register('clientSecret', {
										required: 'Client Secret is required',
									})}
									type='password'
									className='input-field mt-1'
									placeholder='mySuperSecretKey123!'
								/>
								{errors.clientSecret && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.clientSecret.message}
									</p>
								)}
							</div>
						</div>

						{/* Submit */}
						<div>
							<button
								type='submit'
								disabled={loading}
								className='btn-primary w-full'>
								{loading ? (
									<>
										<LoadingSpinner
											size='sm'
											className='mr-2'
										/>
										Creating Account...
									</>
								) : (
									'Create Account'
								)}
							</button>
						</div>

						{/* Terms */}
						<div className='text-center'>
							<p className='text-xs text-gray-500'>
								By creating an account, you agree to our Terms
								of Service and Privacy Policy
							</p>
						</div>
					</form>
				</div>
			</div>

			{showSuccessModal && (
				<RegistrationSuccessModal closeModal={handleCloseModal} />
			)}
		</>
	);
};

export default Register;
